/**
 * Helper موحَّد لإرسال أحداث تثبيت PWA — يلتقط platform/displayMode/locale/market.
 * يبتلع كلّ الأخطاء (تتبّع لا يجب أن يكسر الواجهة).
 */
import { logPwaInstallEvent } from "@/utils/pwaInstall.functions";

const SESSION_KEY = "lecmo_session_id";

function getOrCreateSessionId(): string {
  if (typeof window === "undefined") return "";
  try {
    let id = window.localStorage.getItem(SESSION_KEY);
    if (!id) {
      id = `s_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 10)}`;
      window.localStorage.setItem(SESSION_KEY, id);
    }
    return id;
  } catch {
    return "";
  }
}

function detectPlatform(): string {
  if (typeof navigator === "undefined") return "unknown";
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod/i.test(ua)) return "ios";
  if (/Android/i.test(ua)) return "android";
  if (/Mac/i.test(ua)) return "macos";
  if (/Windows/i.test(ua)) return "windows";
  if (/Linux/i.test(ua)) return "linux";
  return "other";
}

function detectDisplayMode(): string {
  if (typeof window === "undefined") return "browser";
  if (window.matchMedia?.("(display-mode: standalone)").matches) return "standalone";
  if (
    (window.navigator as unknown as { standalone?: boolean }).standalone === true
  ) {
    return "standalone";
  }
  if (window.matchMedia?.("(display-mode: minimal-ui)").matches) return "minimal-ui";
  return "browser";
}

/** علامات خطوات القمع لكلّ جهاز — تمنع التكرار وتضمن الترتيب */
const FUNNEL_STEP_KEY = "lecmo_pwa_funnel_steps";

function readFunnelSteps(): string[] {
  try {
    const raw = window.localStorage.getItem(FUNNEL_STEP_KEY);
    return raw ? (JSON.parse(raw) as string[]) : [];
  } catch {
    return [];
  }
}

function hasFunnelStep(step: string): boolean {
  return readFunnelSteps().includes(step);
}

function markFunnelStep(step: string) {
  try {
    const steps = readFunnelSteps();
    if (!steps.includes(step)) {
      steps.push(step);
      window.localStorage.setItem(FUNNEL_STEP_KEY, JSON.stringify(steps));
    }
  } catch {
    /* تجاهل */
  }
}

export async function trackPwaEvent(

  eventType:
    | "prompt_available"
    | "prompt_accepted"
    | "prompt_dismissed"
    | "installed"
    | "already_installed"
    | "launched_standalone"
    | "card_viewed"
    | "cta_clicked"
    | "ios_hint_opened",
  ctx?: { locale?: string; market?: string; meta?: Record<string, string | number | boolean> },
) {
  if (typeof window === "undefined") return;
  try {
    await logPwaInstallEvent({
      data: {
        eventType,
        sessionId: getOrCreateSessionId(),
        userAgent: navigator.userAgent.slice(0, 500),
        platform: detectPlatform(),
        locale: ctx?.locale,
        market: ctx?.market,
        displayMode: detectDisplayMode(),
        meta: ctx?.meta,
      },
    });
    const stepMap: Record<string, string | null> = {
      card_viewed: "prompt_shown",
      cta_clicked: "prompt_shown",
      ios_hint_opened: "prompt_shown",
      prompt_available: "prompt_shown",
      prompt_accepted: "accepted",
      installed: "accepted",
      prompt_dismissed: "dismissed",
      already_installed: null,
      launched_standalone: null,
    };
    const step = stepMap[eventType];
    if (step) {
      const { trackFunnelEvent } = await import("@/lib/funnelClient");
      const platform = detectPlatform();
      // ترتيب منطقيّ إلزاميّ: لا «إكمال» بدون «مدخل» سابق،
      // ولا تكرار للخطوة نفسها في الجلسة (installed + prompt_accepted معاً).
      if (step !== "prompt_shown" && !hasFunnelStep("prompt_shown")) {
        markFunnelStep("prompt_shown");
        void trackFunnelEvent("pwa_install", "prompt_shown", { platform });
      }
      if (!hasFunnelStep(step)) {
        markFunnelStep(step);
        void trackFunnelEvent("pwa_install", step, { platform });
      }
    }

  } catch {
    // تتبّع صامت
  }
}

const INSTALL_COUNTED_KEY = "lecmo_pwa_install_counted";

/**
 * يعيد true مرّة واحدة فقط لكلّ جهاز — يستخدم لاحتساب أوّل فتح كتطبيق
 * كتثبيت ضمنيّ (يغطّي حالات Android ⋮ و iOS Add to Home Screen التي
 * لا تطلق حدث appinstalled).
 */
export function markInstallCountedOnce(): boolean {
  if (typeof window === "undefined") return false;
  try {
    if (window.localStorage.getItem(INSTALL_COUNTED_KEY)) return false;
    window.localStorage.setItem(INSTALL_COUNTED_KEY, String(Date.now()));
    return true;
  } catch {
    return false;
  }
}
